import { Layout, LayoutAccess, Layouts, Shop } from "@/types/v2";
import { shop } from "@/repositories/shop";
import { arrayUnion, doc, runTransaction, updateDoc } from "firebase/firestore";
import clone from "just-clone";
import { db } from "@/services/db";
import { store } from "@/stores/useStore";

export const layout = {
  generateBaseLayout: (): Layout[] => {
    return [
      { i: "dispatch", x: 0, y: 0, w: 96, h: 2, moved: false, static: false },
    ];
  },
  saveLayouts: async (shopId: string, layouts: Layouts) => {
    return await updateDoc(shop.ref(shopId), { layouts });
  },
  deleteLayout: async (shopId: string, layoutName: string) => {
    const layouts = clone(store.getState().shop?.layouts || []);

    const layoutIndex = layouts.findIndex((l) =>
      Object.keys(l).includes(layoutName)
    );

    layouts.splice(layoutIndex, 1);

    await updateDoc(shop.ref(shopId), { layouts });
    return;
  },
  updateLayout: async (
    shopId: string,
    layoutName: string,
    layoutData: Layout[]
  ) => {
    const layouts = clone(store.getState().shop?.layouts || []);

    const layoutIndex = layouts.findIndex((l) =>
      Object.keys(l).includes(layoutName)
    );

    if (layoutIndex === -1) {
      await layout.addLayout(shopId, layoutName, layoutData);
      return { created: true, updated: false };
    }

    layouts[layoutIndex] = { [layoutName]: layoutData };

    await updateDoc(shop.ref(shopId), { layouts });

    return { created: false, updated: true };
  },
  /**
   * Renames a layout. Layouts are keyed by name inside the `layouts` array
   * (`{ [layoutName]: Layout[] }`), so a rename replaces the object key rather
   * than updating a field -- and the name doubles as the identifier used by
   * `LayoutAccess.layoutName`. Both live on the shop doc, so they are rewritten
   * in a single transaction against the live document: a partial write would
   * silently drop the layout's access list, and reading from the store instead
   * would clobber concurrent layout edits.
   */
  renameLayout: async (shopId: string, oldName: string, newName: string) => {
    if (oldName === newName) return;

    const shopRef = doc(db, `shops/${shopId}`);

    await runTransaction(db, async (transaction) => {
      const shopData = (await transaction.get(shopRef)).data() as
        | Shop
        | undefined;
      const layouts = clone(shopData?.layouts ?? []);

      const layoutIndex = layouts.findIndex((l) =>
        Object.keys(l).includes(oldName)
      );
      if (layoutIndex === -1) {
        throw new Error(`Layout "${oldName}" does not exist`);
      }
      if (layouts.some((l) => Object.keys(l).includes(newName))) {
        throw new Error(`A layout named "${newName}" already exists`);
      }

      layouts[layoutIndex] = { [newName]: layouts[layoutIndex][oldName] };

      const update: { layouts: Layouts; layoutAccess?: LayoutAccess[] } = {
        layouts,
      };

      const layoutAccess = shopData?.layoutAccess ?? [];
      if (layoutAccess.some((access) => access.layoutName === oldName)) {
        update.layoutAccess = layoutAccess.map((access) =>
          access.layoutName === oldName
            ? { ...access, layoutName: newName }
            : access
        );
      }

      transaction.update(shopRef, update);
    });
  },
  addLayout: async (shopId: string, layoutName: string, layout: Layout[]) => {
    await updateDoc(shop.ref(shopId), {
      layouts: arrayUnion({ [layoutName]: layout }),
    });
    return layout;
  },
  addContainerToLayout: (containerId: string, layoutData: Layout[]) => {
    const layoutCopy = layoutData.slice();
    const newLayout: Layout = {
      i: containerId,
      x: 0,
      y: 0,
      w: 16,
      h: 6,
      moved: false,
      static: false,
    };
    return layoutCopy.concat(newLayout);
  },
  /**
   * Bulk version of addContainerToLayout: adds each container id not
   * already present in layoutData, in order, using the same default
   * sizing. Skips ids already in the layout so re-running a bulk "Add to
   * Layout" over an overlapping selection never duplicates a container.
   */
  addContainersToLayout: (containerIds: string[], layoutData: Layout[]) => {
    const existingIds = new Set(layoutData.map((item) => item.i));
    return containerIds
      .filter((id) => !existingIds.has(id))
      .reduce((acc, id) => layout.addContainerToLayout(id, acc), layoutData);
  },
  removeContainerFromLayout: (containerId: string, layoutData: Layout[]) => {
    const layoutCopy = layoutData.slice();
    return layoutCopy.filter((item) => item.i !== containerId);
  },
  removeContainerFromAllLayouts: (containerId: string) => {
    const layouts = clone(store.getState().shop?.layouts || []);
    for (let i = 0; i < layouts.length; i++) {
      const layoutData = layouts[i];
      const keys = Object.keys(layoutData);
      for (let j = 0; j < keys.length; j++) {
        const layoutName = keys[j];
        Object.assign(
          layoutData[layoutName],
          layout.removeContainerFromLayout(containerId, layoutData[layoutName])
        );
      }
      layouts[i] = layoutData;
    }
    return layouts;
  },
  findLayoutsWithContainer: (
    containerId: string,
    layouts: Layouts
  ): Layouts => {
    const matching: Layouts = [];
    for (let i = 0; i < layouts.length; i++) {
      const layoutData = layouts[i];
      const keys = Object.keys(layoutData);
      for (let j = 0; j < keys.length; j++) {
        const layoutName = keys[j];
        const layout = layoutData[layoutName];
        const containerIndex = layout.findIndex((l) => l.i === containerId);
        if (containerIndex !== -1) {
          matching.push(layoutData);
        }
      }
    }
    return matching;
  },
  moveLayoutUp: (layoutName: string) => {
    const layouts = clone(store.getState().shop?.layouts || []);
    const layoutIndex = layouts.findIndex((l) =>
      Object.keys(l).includes(layoutName)
    );
    if (layoutIndex === -1) return;
    const layout = layouts[layoutIndex];
    layouts.splice(layoutIndex, 1);
    layouts.splice(layoutIndex - 1, 0, layout);
    return layouts;
  },
  moveLayoutDown: (layoutName: string) => {
    const layouts = clone(store.getState().shop?.layouts || []);
    const layoutIndex = layouts.findIndex((l) =>
      Object.keys(l).includes(layoutName)
    );
    if (layoutIndex === -1) return;
    const layout = layouts[layoutIndex];
    layouts.splice(layoutIndex, 1);
    layouts.splice(layoutIndex + 1, 0, layout);
    return layouts;
  },
  filterForAccess: (
    shop: undefined | Shop,
    employeeId: string | undefined,
    isSuperAdmin: boolean | undefined
  ) => {
    if (!shop) return [];

    // Check if layoutAccess and userId are available
    if (!shop?.layoutAccess || !employeeId) {
      return shop?.layouts; // If no access data or user ID, return all layouts
    }

    if (isSuperAdmin) {
      return shop?.layouts;
    }

    const layoutAccessArray = shop.layoutAccess;

    // Filter layouts based on user access
    return shop.layouts.filter((layout) => {
      const layoutName = Object.keys(layout)[0]; // Get layout name
      const accessEntry = layoutAccessArray.find(
        (access) => access.layoutName === layoutName
      );

      // If no accessEntry or allowedList is empty, allow access
      if (!accessEntry || accessEntry.allowedList.length === 0) {
        return true;
      }

      // Allow access if userId is in the allowedList
      return accessEntry.allowedList.includes(employeeId);
    });
  },
};
